import 'react-datepicker/dist/react-datepicker.css';
import './PunchupInputDateAndTime.css';

import { ChangeEvent, FC, ReactNode, useState } from 'react';

import DatePicker from 'react-datepicker';
import FlexContainer from 'features/common/components/FlexContainer/FlexContainer';
import { FormInputDateAndTimeProps } from 'features/common/components/Form/types';
import { Icon } from 'features/common/components/Icon';
import { InputLabel } from 'features/common/components/Form/InputLabel';
import { ZERO_TIME } from 'utils/constants';
import { handleNewDateTime } from 'features/common/components/Form/utils';
import { get, useFormContext } from 'react-hook-form';
import styles from './PunchupInputDateAndTime.module.css';

const CalendarContainer = ({ children }: { children: ReactNode }) => (
	<div className={styles.calendarContainer}>{children}</div>
);

const PunchupFormInputDateAndTime: FC<FormInputDateAndTimeProps> = ({
	id,
	name,
	label,
	onChange,
	index,
	value,
	isFromRequiredFields,
	calcErrors,
	origin,
}: FormInputDateAndTimeProps) => {
	const {
		getValues,
		setValue,
		formState: { errors },
		watch,
	} = useFormContext();

	const isInputArray = index !== undefined;
	const error = isInputArray ? calcErrors : get(errors, name)?.message;
	const [, setIsCalendarOpen] = useState<boolean>(false);

	const _value = watch(name);

	const handleOnChangeDate = (event: Date | null) => {
		if (event) {
			if (isInputArray) {
				const nextDates = handleNewDateTime({
					event,
					currentDates: getValues(name),
					index: Number(index),
					label: id,
				});
				setValue(name, nextDates);
			} else {
				setValue(name, event);
			}
		}
		/* react-datepicker emits a Date | null; the shared onChange prop is typed as a
		   ChangeEvent (a pre-existing mismatch owned by the out-of-zone Form/types.ts). */
		onChange && onChange(event as unknown as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>);
	};

	const handleCalendarStatus = () => {
		setIsCalendarOpen((prevState) => !prevState);
	};

	const handleValue = () => {
		if (value === null || value === undefined) {
			const zeroTime = _value === ZERO_TIME;
			if (zeroTime) return null;
			else return _value;
		} else return value;
	};

	const containerClass = [
		styles.inputContainer,
		'punchup-input-date-time',
		error ? styles.inputContainerError : '',
		origin === 'event-creation' ? origin : '',
	]
		.filter(Boolean)
		.join(' ');

	return (
		<FlexContainer flexDirection="column" width="100%">
			<InputLabel className={isInputArray ? 'SM' : ''} htmlFor={id ?? name} label={label} />
			<div className={containerClass}>
				<div className={isFromRequiredFields ? 'requiredFields' : ''}>
					<DatePicker
						autoComplete="off"
						id={id ?? name}
						selected={handleValue()}
						onChange={handleOnChangeDate}
						showTimeSelect
						dateFormat="d MMMM, yyyy h:mm aa"
						calendarContainer={CalendarContainer}
						onCalendarOpen={handleCalendarStatus}
						onCalendarClose={handleCalendarStatus}
						data-cy="test"
					/>
				</div>
				<Icon iconName={`${origin === 'event-creation' ? 'blue' : 'green'}Calendar`} className={styles.calendarIcon} />
			</div>
			{error && <span className="punchupFormError">{error}</span>}
		</FlexContainer>
	);
};

export { PunchupFormInputDateAndTime };
